import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { 
  Check, Users, Target, Briefcase, Shield, Zap, 
  CreditCard, MessageSquare, BarChart3, FileText,
  TrendingUp, Calendar, Award, Rocket
} from 'lucide-react';

function BusinessPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroFeatures = [
    "CRM Personalizado",
    "Gestión de Proyectos",
    "Facturación Inteligente",
    "Reportería en Tiempo Real"
  ];

  const coreModules = [
    {
      icon: Target,
      title: "CRM Avanzado",
      description: "Gestiona todo el ciclo de vida del cliente desde el primer contacto hasta la fidelización. Pipeline de ventas optimizado.",
      modules: ["Leads y Oportunidades", "Gestión de Clientes", "Cotizaciones", "Seguimiento automático"]
    },
    {
      icon: Briefcase,
      title: "Gestión de Proyectos",
      description: "Organiza, asigna y da seguimiento a proyectos y tareas de tu equipo en tiempo real.",
      modules: ["Proyectos", "Tareas", "Colaboración en equipo", "Seguimiento de tiempo"]
    },
    {
      icon: Users,
      title: "Gestión de Recursos",
      description: "Administra tu equipo, espacios, equipos y activos desde una plataforma centralizada.",
      modules: ["Gestión de empleados", "Control de recursos", "Espacios y equipamiento", "Disponibilidad"]
    },
    {
      icon: CreditCard,
      title: "Facturación Electrónica",
      description: "Facturación automática con DTE en Chile. Seguimiento de pagos y cobranza automatizada.",
      modules: ["Facturación electrónica", "Control de pagos", "Cobranza automática", "Conciliación bancaria"]
    },
    {
      icon: BarChart3,
      title: "Reportería & Analytics",
      description: "Dashboards personalizables con métricas en tiempo real para tomar decisiones basadas en datos.",
      modules: ["KPIs personalizados", "Reportes automáticos", "Análisis de ventas", "Forecasting"]
    },
    {
      icon: MessageSquare,
      title: "Comunicación Integrada",
      description: "WhatsApp Business, email marketing y sistema de tickets para comunicación efectiva con clientes.",
      modules: ["WhatsApp Business", "Email Marketing", "Tickets de soporte", "Notificaciones automáticas"]
    }
  ];

  const benefits = [
    {
      title: "Aumenta tus Ventas",
      description: "Pipeline de ventas optimizado con seguimiento automático que convierte más leads en clientes.",
      icon: TrendingUp
    },
    {
      title: "Automatiza Procesos",
      description: "Cotizaciones, facturación, cobranza y seguimiento 100% automatizados. Ahorra tiempo y reduce errores.",
      icon: Zap
    },
    {
      title: "Decisiones Informadas",
      description: "Dashboards con métricas en tiempo real para identificar oportunidades y optimizar resultados.",
      icon: BarChart3
    },
    {
      title: "Escalabilidad",
      description: "Plataforma que crece con tu empresa. Desde 5 hasta 500+ usuarios sin límites.",
      icon: Rocket
    }
  ];

  const includedModules = [
    "✅ CRM con Pipeline de Ventas",
    "✅ Gestión de Clientes 360°",
    "✅ Cotizaciones Automáticas",
    "✅ Contratos & Firma Digital",
    "✅ Gestión de Proyectos & Tareas",
    "✅ Control de Recursos y Equipos",
    "✅ Facturación Electrónica (DTE)",
    "✅ WhatsApp Business Integrado",
    "✅ Email Marketing & Campañas",
    "✅ Reportería y Dashboards",
    "✅ Sistema de Tickets",
    "✅ Integraciones API"
  ];

  return (
    <>
      <SEOHead
        title="Cloudbase para Pymes y Empresas - CRM y Gestión Empresarial"
        description="Plataforma integral para pymes y empresas: CRM avanzado, gestión de proyectos, facturación electrónica, reportería en tiempo real."
        keywords="CRM empresas chile, software gestion empresarial, facturacion electronica pymes, gestion proyectos"
        path="/empresas"
        breadcrumbs={[
          { name: 'Inicio', item: '/' },
          { name: 'Industrias', item: '/#industrias' },
          { name: 'Pymes y Empresas', item: '/empresas' }
        ]}
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-black relative overflow-hidden min-h-screen flex items-center">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <div className="inline-block mb-6 px-4 py-2 bg-green-600/20 border border-green-600/30 text-green-400 rounded-full text-sm font-semibold">
                Solución para Pymes y Empresas
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
                La Plataforma que hace Crecer tu Empresa
              </h1>
              
              <p className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
                CRM, gestión de proyectos, facturación electrónica y reportería. Todo integrado en una sola plataforma diseñada para empresas chilenas.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
                <Button 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Ver Demo Personalizada
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Hablar con un Experto
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
                {heroFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Core Modules Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                  Módulos Incluidos
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Todos los módulos que necesitas para gestionar tu empresa de manera eficiente
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {coreModules.map((module, index) => {
                  const IconComponent = module.icon;
                  return (
                    <div 
                      key={index}
                      className="bg-gray-50 p-8 rounded-lg hover:shadow-lg transition-shadow"
                    >
                      <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                        <IconComponent className="w-7 h-7 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {module.title}
                      </h3>
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {module.description}
                      </p>
                      <div className="space-y-2">
                        {module.modules.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                  Beneficios Reales para tu Empresa
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Resultados medibles desde el primer mes
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {benefits.map((benefit, index) => {
                  const IconComponent = benefit.icon;
                  return (
                    <div 
                      key={index}
                      className="text-center"
                    >
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <IconComponent className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Included Features */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                  Todo Incluido
                </h2>
                <p className="text-xl text-gray-600">
                  Sin costos ocultos. Todos estos módulos incluidos en tu plan
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {includedModules.map((module, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 text-lg text-gray-700"
                  >
                    <span>{module}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-green-600 to-green-700">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                ¿Listo para hacer crecer tu empresa?
              </h2>
              <p className="text-xl text-white/90 mb-10">
                Agenda una demo personalizada y descubre cómo Cloudbase puede transformar tu negocio
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-white text-green-600 hover:bg-gray-100 px-10 py-7 text-base rounded font-semibold"
                  onClick={() => window.location.href = '/demo'}
                >
                  Solicitar Demo Gratis
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-10 py-7 text-base rounded font-semibold"
                  onClick={() => window.location.href = '/demo'}
                >
                  Ver Casos de Éxito
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default BusinessPage;
